import { Fragment } from 'react';
import { CircleCheckIcon, CircleDashedIcon } from 'lucide-react';
import {
  DUURT_LANG_TEKST,
  FASE_LABEL,
  VERWERKINGSFASEN,
  duurtLang,
  faseStand,
  type Verwerkingsfase,
} from '@vip/shared';

import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

/**
 * De vier stappen tussen "uploaden" en "jij bent aan zet", met de stap die nu
 * loopt eruit gelicht.
 *
 * Hiervóór stond er één regel: "Opname omzetten…". Die klopte maar veranderde
 * niet, en een tekst die niet verandert leest als een vastloper — je zag niet of
 * er iets gebeurde, hoe ver het was of wat er nog kwam.
 *
 * Dezelfde beeldtaal als `Spoor` op de gesprekskaart: afgevinkt in de merkkleur,
 * de lopende stap met een draaiend rondje, wat nog komt gestippeld. De
 * standberekening is wél een andere — die van de kaart rekent met de status van
 * het gesprek en vier grovere stappen, deze met de fase van de verwerking.
 *
 * Staat op twee plekken: in het venster tijdens het uploaden en op de
 * transcriptpagina daarna. De buddy ziet dezelfde balk doorlopen; alleen het
 * scherm wisselt onderweg.
 */
export function Verwerkingsspoor({
  actief,
  bijschrift,
  className,
  secondenInStap = 0,
}: {
  actief: Verwerkingsfase;
  /** Wat deze stap op dit moment te melden heeft: een percentage, een aantal
   *  tekens, of de tijd die hij al loopt. */
  bijschrift?: string;
  /** Hoe lang de lopende stap al bezig is. Voorbij een minuut komt eronder te
   *  staan dat lang hier normaal is. */
  secondenInStap?: number;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Alleen de naam van de lopende stap wordt aangekondigd. De balk en het
          bijschrift niet: daar tikt een teller in mee, en die elke seconde laten
          voorlezen maakt het scherm onbruikbaar met een schermlezer. */}
      <span className="sr-only" aria-live="polite">
        {FASE_LABEL[actief]}
      </span>

      <div className="flex items-center gap-2">
        {VERWERKINGSFASEN.map((fase, i) => {
          const s = faseStand(actief, fase);
          const Icoon =
            s === 'bezig' ? Spinner : s === 'klaar' ? CircleCheckIcon : CircleDashedIcon;
          return (
            <Fragment key={fase}>
              <div className="flex items-center gap-1.5">
                <Icoon
                  className={cn(
                    'size-3.5 shrink-0',
                    s === 'open' ? 'text-muted-foreground/40' : 'text-primary',
                  )}
                />
                {/* Op een telefoon passen vier woorden niet naast elkaar; dan
                    blijft alleen het label van de lopende stap staan. De rest
                    wordt wél voorgelezen. */}
                <span
                  className={cn(
                    'text-xs whitespace-nowrap',
                    s === 'bezig'
                      ? 'font-medium text-foreground'
                      : 'text-muted-foreground sr-only sm:not-sr-only',
                  )}
                >
                  {FASE_LABEL[fase]}
                </span>
              </div>
              {i < VERWERKINGSFASEN.length - 1 && (
                <Separator className={cn('min-w-3 flex-1', s === 'klaar' && 'bg-primary/30')} />
              )}
            </Fragment>
          );
        })}
      </div>

      {(bijschrift || duurtLang(secondenInStap)) && (
        <p className="text-sm text-muted-foreground tabular-nums">
          {bijschrift}
          {bijschrift && duurtLang(secondenInStap) && ' · '}
          {duurtLang(secondenInStap) && DUURT_LANG_TEKST}
        </p>
      )}
    </div>
  );
}
